import React, { useState, useRef, useContext } from 'react';
import { AuthContext } from '../../AuthContext';
import * as Realm from "realm-web";

const app = new Realm.App({ id: "thebrainfund-qyrogsg" });

function ModelBuilder({ currentModel, setCurrentModel, onRunBacktest }) {
  const { user } = useContext(AuthContext);
  const [modelName, setModelName] = useState(currentModel?.name || 'Untitled Model');
  const [modelDescription, setModelDescription] = useState(currentModel?.description || '');
  const [modelIcon, setModelIcon] = useState(currentModel?.icon || '🎯');
  const [modelDifficulty, setModelDifficulty] = useState(currentModel?.difficulty || 'Beginner');
  const [modelType, setModelType] = useState(currentModel?.type || 'Multi-Sport');
  const [modelPrice, setModelPrice] = useState(currentModel?.price || 0);
  const [isPublic, setIsPublic] = useState(currentModel?.isPublic || false);
  const [selectedTags, setSelectedTags] = useState(currentModel?.tags || []);
  const [draggedVariable, setDraggedVariable] = useState(null);
  const [modelVariables, setModelVariables] = useState(currentModel?.variables || []);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const canvasRef = useRef(null);

  // Available tags for models
  const availableTags = [
    { id: 'value-betting', name: 'Value Betting', color: '#10b981' },
    { id: 'contrarian', name: 'Contrarian', color: '#8b5cf6' },
    { id: 'momentum', name: 'Momentum', color: '#f59e0b' },
    { id: 'weather-based', name: 'Weather Based', color: '#06b6d4' },
    { id: 'injury-focused', name: 'Injury Focused', color: '#ef4444' },
    { id: 'home-advantage', name: 'Home Advantage', color: '#84cc16' },
    { id: 'travel-fatigue', name: 'Travel Fatigue', color: '#f97316' },
    { id: 'sharp-money', name: 'Sharp Money', color: '#6366f1' },
    { id: 'public-fade', name: 'Public Fade', color: '#ec4899' },
    { id: 'line-movement', name: 'Line Movement', color: '#14b8a6' },
    { id: 'totals-focused', name: 'Totals Focused', color: '#a855f7' },
    { id: 'spread-focused', name: 'Spread Focused', color: '#3b82f6' },
    { id: 'experimental', name: 'Experimental', color: '#64748b' },
    { id: 'high-volume', name: 'High Volume', color: '#dc2626' },
    { id: 'conservative', name: 'Conservative', color: '#16a34a' },
    { id: 'aggressive', name: 'Aggressive', color: '#ea580c' }
  ];

  // Available icons for models
  const availableIcons = [
    '🎯', '📈', '🌦️', '🔄', '🏥', '✈️', '⚡', '🔥', '💎', '🚀', 
    '🎲', '📊', '🏀', '🏈', '⚽', '🎾', '🏒', '⚾', '🎱', '🎪'
  ];

  // Available difficulties
  const difficulties = ['Beginner', 'Intermediate', 'Advanced'];

  // Available sports/types
  const modelTypes = [
    'Multi-Sport', 'NBA', 'NFL', 'MLB', 'NHL', 'Soccer', 'Tennis', 
    'College Basketball', 'College Football', 'MMA', 'Boxing', 'Golf'
  ];

  // Available variables for model building
  const variableCategories = [
    {
      name: 'Team Performance',
      variables: [
        { id: 'home_win_rate', name: 'Home Win Rate', description: 'Team win percentage at home' },
        { id: 'away_win_rate', name: 'Away Win Rate', description: 'Team win percentage on the road' },
        { id: 'recent_form', name: 'Recent Form', description: 'Performance over last 5 games' },
        { id: 'head_to_head', name: 'Head-to-Head', description: 'Historical matchup results' },
      ]
    },
    {
      name: 'Market Data',
      variables: [
        { id: 'opening_line', name: 'Opening Line', description: 'Initial betting line' },
        { id: 'line_movement', name: 'Line Movement', description: 'How the line has moved' },
        { id: 'public_betting', name: 'Public Betting %', description: 'Percentage of bets on each side' },
        { id: 'sharp_money', name: 'Sharp Money', description: 'Professional betting activity' },
      ]
    },
    {
      name: 'External Factors',
      variables: [
        { id: 'weather', name: 'Weather', description: 'Game day weather conditions' },
        { id: 'injuries', name: 'Key Injuries', description: 'Important player availability' },
        { id: 'rest_days', name: 'Rest Days', description: 'Days since last game' },
        { id: 'travel', name: 'Travel Distance', description: 'Miles traveled for away team' },
      ]
    }
  ];

  const handleDragStart = (e, variable) => {
    setDraggedVariable(variable);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedVariable) {
      const newVariable = {
        ...draggedVariable,
        weight: 50, // Default weight
        id: `${draggedVariable.id}_${Date.now()}` // Unique ID for this instance
      };
      setModelVariables(prev => [...prev, newVariable]);
      setDraggedVariable(null);
    }
  };

  const removeVariable = (variableId) => {
    setModelVariables(prev => prev.filter(v => v.id !== variableId));
  };

  const updateVariableWeight = (variableId, weight) => {
    setModelVariables(prev => 
      prev.map(v => v.id === variableId ? { ...v, weight: parseInt(weight) } : v)
    );
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const saveModel = async () => {
    if (!user) {
      setSaveMessage('❌ Please login to save models');
      return;
    }

    if (modelVariables.length === 0) {
      setSaveMessage('❌ Add variables before saving');
      return;
    }

    setIsSaving(true);
    setSaveMessage('💾 Saving model...');

    try {
      const modelData = {
        name: modelName.trim(),
        description: modelDescription.trim() || `Custom prediction model with ${modelVariables.length} variables`,
        icon: modelIcon,
        difficulty: modelDifficulty,
        type: modelType,
        price: parseFloat(modelPrice) || 0,
        isPublic: isPublic,
        variables: modelVariables.map(variable => ({
          id: variable.id,
          name: variable.name,
          weight: variable.weight,
          description: variable.description,
          category: getVariableCategory(variable.id) || 'Unknown'
        })),
        userId: user.id,
        tags: selectedTags
      };

      const result = await app.currentUser.functions.saveModel(modelData);
      
      if (result.success) {
        setSaveMessage(`✅ ${result.message}`);
        setCurrentModel({
          ...modelData,
          _id: result.modelId,
          created: new Date().toISOString()
        });
        
        // Clear message after 3 seconds
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        setSaveMessage(`❌ ${result.error}`);
        console.error('Save failed:', result.error);
      }
    } catch (error) {
      setSaveMessage('❌ Failed to save model');
      console.error('Error saving model:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to get variable category
  const getVariableCategory = (variableId) => {
    const baseId = variableId.split('_').slice(0, -1).join('_');
    for (const category of variableCategories) {
      const found = category.variables.find(v => v.id === baseId);
      if (found) return category.name;
    }
    return 'Unknown';
  };

  const runBacktest = () => {
    const model = {
      name: modelName,
      variables: modelVariables,
      created: new Date().toISOString(),
      type: 'custom'
    };
    onRunBacktest(model);
  };

  return (
    <div className="model-builder">
      {/* Variable Panel */}
      <div className="variable-panel">
        <h3>
          <span>📊</span>
          Available Variables
        </h3>
        
        {variableCategories.map(category => (
          <div key={category.name} className="variable-category">
            <h4>{category.name}</h4>
            <div className="variable-list">
              {category.variables.map(variable => (
                <div
                  key={variable.id}
                  className="variable-item"
                  draggable
                  onDragStart={(e) => handleDragStart(e, variable)}
                >
                  <div className="variable-name">{variable.name}</div>
                  <div className="variable-description">{variable.description}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Model Canvas */}
      <div className="model-canvas">
        <div className="canvas-header">
          <h3>Model Canvas</h3>
        </div>
        <div 
          className="canvas-area"
          ref={canvasRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {modelVariables.length === 0 ? (
            <div className="canvas-placeholder">
              <div className="canvas-placeholder-icon">🎯</div>
              <h4>Start Building Your Model</h4>
              <p>Drag variables from the left panel to build your prediction model</p>
            </div>
          ) : (
            <div className="model-variables">
              {modelVariables.map(variable => (
                <div key={variable.id} className="canvas-variable">
                  <div className="variable-header">
                    <span className="variable-title">{variable.name}</span>
                    <button 
                      className="remove-variable"
                      onClick={() => removeVariable(variable.id)}
                    >
                      ✕
                    </button>
                  </div>
                  <div className="variable-weight">
                    <label>Weight: {variable.weight}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={variable.weight}
                      onChange={(e) => updateVariableWeight(variable.id, e.target.value)}
                      className="weight-slider"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Model Controls */}
      <div className="model-controls">
        <h3>
          <span>⚙️</span>
          Model Settings
        </h3>
        
        <div className="control-section">
          <h4>Model Name</h4>
          <input
            type="text"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            placeholder="Enter model name"
            className="model-name-input"
          />
        </div>

        <div className="control-section">
          <h4>Description</h4>
          <textarea
            value={modelDescription}
            onChange={(e) => setModelDescription(e.target.value)}
            placeholder="Describe your prediction model strategy and approach..."
            className="model-description-input"
            rows="3"
          />
        </div>

        <div className="control-section">
          <h4>Model Details</h4>
          <div className="model-details-grid">
            <div className="detail-group">
              <label>Icon</label>
              <select 
                value={modelIcon} 
                onChange={(e) => setModelIcon(e.target.value)}
                className="model-select"
              >
                {availableIcons.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>
            <div className="detail-group">
              <label>Difficulty</label>
              <select 
                value={modelDifficulty} 
                onChange={(e) => setModelDifficulty(e.target.value)}
                className="model-select"
              >
                {difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>{difficulty}</option>
                ))}
              </select>
            </div>
            <div className="detail-group">
              <label>Sport/Type</label>
              <select 
                value={modelType} 
                onChange={(e) => setModelType(e.target.value)}
                className="model-select"
              >
                {modelTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="detail-group">
              <label>Price ($)</label>
              <input
                type="number"
                value={modelPrice}
                onChange={(e) => setModelPrice(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="model-price-input"
              />
            </div>
          </div>
          <div className="model-public-toggle">
            <label className="toggle-label">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="toggle-checkbox"
              />
              <span className="toggle-text">Make model public (visible to community)</span>
            </label>
          </div>
        </div>

        <div className="control-section">
          <h4>Tags ({selectedTags.length} selected)</h4>
          <div className="tags-grid">
            {availableTags.map(tag => (
              <button
                key={tag.id}
                className={`tag-item ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                onClick={() => toggleTag(tag.id)}
                style={{
                  '--tag-color': tag.color,
                  backgroundColor: selectedTags.includes(tag.id) ? tag.color : 'transparent',
                  borderColor: tag.color,
                  color: selectedTags.includes(tag.id) ? 'white' : tag.color
                }}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>

        <div className="control-section">
          <h4>Model Stats</h4>
          <div className="model-stats">
            <div className="stat-item">
              <span className="stat-label">Variables:</span>
              <span className="stat-value">{modelVariables.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Weight:</span>
              <span className="stat-value">
                {modelVariables.reduce((sum, v) => sum + v.weight, 0)}%
              </span>
            </div>
          </div>
        </div>

        <div className="control-section">
          <h4>Actions</h4>
          {saveMessage && (
            <div className={`save-message ${saveMessage.includes('✅') ? 'success' : saveMessage.includes('❌') ? 'error' : 'info'}`}>
              {saveMessage}
            </div>
          )}
          <div className="control-buttons">
            <button 
              className="btn-secondary"
              onClick={saveModel}
              disabled={modelVariables.length === 0 || isSaving || !user}
            >
              {isSaving ? '💾 Saving...' : '💾 Save Model'}
            </button>
            <button 
              className="btn-primary"
              onClick={runBacktest}
              disabled={modelVariables.length === 0}
            >
              🚀 Run Backtest
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModelBuilder;
